import { pipe } from 'ramda';

import Literature from '../ProductSections/Literature';
import RelatedArticles from '../RelatedNewsSection';
import RelatedProducts from '../RelatedProductsSection';
import SpecsSection from '../ProductSections/SpecsSection';

export const addLiteratureSection = (product, literatureCategories) => (sections = []) => {
  if (product.literature && product.literature.length > 0) {
    return [
      ...sections,
      {
        data: {
          literature: compileLitData(product.literature, literatureCategories),
          productSlug: product.slug
        },
        id: 'lit',
        label: 'Literature'
      }
    ];
  }

  return sections;
};

export const addRelatedArticlesSection = product => (sections = []) => {
  if (product.relatedArticles && product.relatedArticles.length > 0) {
    return [
      ...sections,
      {
        data: {
          articles: product.relatedArticles
        },
        id: 'articles',
        label: 'Related Articles'
      }
    ];
  }

  return sections;
};

export const addRelatedProductsSection = product => (sections = []) => {
  if (product.relatedProducts && product.relatedProducts.length > 0) {
    return [
      ...sections,
      {
        data: {
          products: product.relatedProducts
        },
        id: 'products',
        label: 'Related Products'
      }
    ];
  }

  return sections;
};

export const addSpecSection = product => (sections = []) => {
  if (product.specifications && product.specifications.length > 0) {
    const specs = product.specifications.filter(
      spec =>
        spec.images &&
        spec.images.length > 0 &&
        spec.table &&
        spec.table.length > 0
    );
    if (specs.length > 0) {
      return [
        ...sections,
        {
          data: { specs },
          id: 'specs',
          label: 'Specifications'
        }
      ];
    }
  }
  return sections;
};

const sortLitCategories = array =>
  array.sort((a, b) => {
    if (a.rank < b.rank) {
      return -1;
    }
    if (a.rank > b.rank) {
      return 1;
    }
    return 0;
  });

export const compileLitData = (literature = [], categories) => {
  if (literature === null) {
    return [[], []];
  }

  const [lit, subLit] = literature.reduce(
    ([lit, subLit], cur) => {
      if (cur === null) return [lit, subLit];
      if (!cur.enabled) return [lit, subLit];
      if (cur.category === null) return [lit, subLit];
      // this is a top level literature item
      const isTopLevel = categories.some(cat => cat.id === cur.category.id);

      if (isTopLevel) {
        // determine if we already have existing items of the same category in lit
        const exists = lit.findIndex(
          lits => lits.categoryId === cur.category.id
        );
        // if it does push it into the existing lit category
        if (exists !== -1) {
          lit[exists].literature.push(cur);
          return [lit, subLit];
        }
        // if it does not exist add a new category to the lit
        return [
          [
            ...lit,
            {
              categoryId: cur.category.id,
              categoryName: cur.category.name,
              subCategories: cur.category.subcategories.map(({ id }) => id),
              literature: [cur],
              rank: cur.category.rank
            }
          ],
          subLit
        ];
      }

      // if sub category literature

      // if the sub category already exists
      const subExists = subLit.findIndex(
        subLits => subLits.categoryId === cur.category.id
      );
      // push it into the existing sub literature category
      if (subExists !== -1) {
        subLit[subExists].literature.push(cur);

        return [lit, subLit];
      }

      // Need to find the parent for sub categorized lit items so we can make sure it exists in the lit array
      const parent = categories.find(cat => {
        const category = cat.subcategories.some(
          subCat => subCat.id === cur.category.id
        );

        if (category) {
          return cat;
        }
        return false;
      });

      // We need to add the parent to the lit array if it doesn't already exist their due to all of its literature items
      // being assigned to sub categories. That way we still get the top level accordion to show up
      if (parent && !lit.some(item => item.categoryId === parent.id)) {
        return [
          [
            ...lit,
            {
              categoryId: parent.id,
              categoryName: parent.name,
              subCategories: parent.subcategories.map(({ id }) => id),
              literature: [],
              rank: parent.rank
            }
          ],
          [
            ...subLit,
            {
              categoryId: cur.category.id,
              categoryName: cur.category.name,
              literature: [cur],
              rank: cur.category.rank
            }
          ]
        ];
      }
      // if it does not not exist add new category to the sub literature
      return [
        lit,
        [
          ...subLit,
          {
            categoryId: cur.category.id,
            categoryName: cur.category.name,
            literature: [cur],
            rank: cur.category.rank
          }
        ]
      ];
    },
    [[], []]
  );

  return [sortLitCategories(lit), sortLitCategories(subLit)];
};

export const createSectionData = (product, literatureCategories) =>
  pipe(
    addSpecSection(product),
    addLiteratureSection(product, literatureCategories),
    addRelatedProductsSection(product),
    addRelatedArticlesSection(product)
  )();

export const reconcileSectionComponent = id => {
  switch (id) {
    case 'articles':
      return RelatedArticles;
    case 'lit':
      return Literature;
    case 'products':
      return RelatedProducts;
    case 'specs':
      return SpecsSection;
    default:
      throw new Error('Provided product section ID did not match a component');
  }
};
